import { DetailsPanel, LoadingIcon } from '@/components/controls';
import { useSubject, useUserInfo } from '@/hooks';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
  Folder as FolderIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
} from '@mui/icons-material';
import {
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';

export function SuspectOccurrence({ personId, occurrence, onOptionClick }) {
  const subjectType = 'SUSPECT';
  const occurrenceId = occurrence?.id;
  const {
    query: {
      data: subject,
      isLoading: subjectLoading,
      isFetching: subjectFetching,
    },
  } = useSubject(subjectType, personId, occurrenceId);
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();
  const canEdit = ['ADMIN', 'SUPERVISOR'].includes(briefingRole);

  return (
    <Stack spacing={1} sx={{ pt: 1 }}>
      <Divider />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="body3" color="textSecondary" sx={{ flexGrow: 1 }}>
          {occurrence?.number}
        </Typography>
        <LoadingIcon isLoading={subjectLoading || subjectFetching} />
        {canEdit && (
          <Tooltip
            title={subject?.highPriority ? 'Deprioritise' : 'Prioritise'}
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                personId,
                occurrenceId,
                subject?.code,
                'highPriority',
              )}
            >
              {subject?.highPriority ? (
                <KeyboardDoubleArrowDownIcon sx={{ fontSize: 'inherit' }} />
              ) : (
                <KeyboardDoubleArrowUpIcon sx={{ fontSize: 'inherit' }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="subtitle2">
          {occurrence?.reportedTime &&
            format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')}
        </Typography>
      </Stack>
      {occurrence?.wanted && (
        <Stack direction="row" spacing={1} sx={{ overflow: 'scroll' }}>
          <Chip label="Wanted" size="small" />
        </Stack>
      )}
      {occurrence?.summary && (
        <Typography variant="body2" sx={{ p: 0.5 }}>
          {occurrence.summary}
        </Typography>
      )}
      <DetailsPanel
        entries={[
          {
            label: 'Last victim update',
            value: occurrence?.lastVictimUpdatedTime
              ? format(occurrence.lastVictimUpdatedTime, 'dd/MM/yyyy HH:mm')
              : '',
          },
          {
            label: 'Last log entry',
            value: occurrence?.lastLogEntryTime
              ? format(occurrence.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
              : '',
          },
        ]}
      />
      <DetailsPanel
        title="Owned by"
        entries={[
          {
            label: 'OIC',
            value: occurrence?.officerInCase?.collarNumber,
          },
          {
            label: 'Team',
            value: occurrence?.officerInCase?.team,
          },
        ]}
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
      >
        <Tooltip
          title={
            subject?.pncCirculated ? (
              <Stack>
                <Typography sx={{ fontSize: 11 }}>Circulated on PNC</Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {subject.pncCirculated.username}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {format(subject.pncCirculated.time, 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Stack>
            ) : (
              'Not circulated on PNC'
            )
          }
        >
          <IconButton
            size="small"
            onClick={
              canEdit
                ? onOptionClick(
                    subjectType,
                    personId,
                    occurrenceId,
                    subject?.code,
                    'pncCirculated',
                  )
                : undefined
            }
            color={subject?.pncCirculated ? 'primary' : 'default'}
          >
            <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            subject?.fileReady ? (
              <Stack>
                <Typography sx={{ fontSize: 11 }}>File ready</Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {subject.fileReady.username}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {format(subject.fileReady.time, 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Stack>
            ) : (
              'File not ready'
            )
          }
        >
          <IconButton
            size="small"
            onClick={
              canEdit
                ? onOptionClick(
                    subjectType,
                    personId,
                    occurrenceId,
                    subject?.code,
                    'fileReady',
                  )
                : undefined
            }
            color={subject?.fileReady ? 'primary' : 'default'}
          >
            <FolderIcon sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            subject?.socialMediaCirculated ? (
              <Stack>
                <Typography sx={{ fontSize: 11 }}>
                  Circulated on social media
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {subject.socialMediaCirculated.username}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {format(
                    subject.socialMediaCirculated.time,
                    'dd/MM/yyyy HH:mm',
                  )}
                </Typography>
              </Stack>
            ) : (
              'Not circulated on social media'
            )
          }
        >
          <IconButton
            size="small"
            onClick={
              canEdit
                ? onOptionClick(
                    subjectType,
                    personId,
                    occurrenceId,
                    subject?.code,
                    'socialMediaCirculated',
                  )
                : undefined
            }
            color={subject?.socialMediaCirculated ? 'primary' : 'default'}
          >
            <FacebookIcon sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            subject?.taser ? (
              <Stack>
                <Typography sx={{ fontSize: 11 }}>
                  Taser officers only
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {subject.taser.username}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {format(subject.taser.time, 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Stack>
            ) : (
              'No Taser authority'
            )
          }
        >
          <IconButton
            size="small"
            onClick={
              canEdit
                ? onOptionClick(
                    subjectType,
                    personId,
                    occurrenceId,
                    subject?.code,
                    'taser',
                  )
                : undefined
            }
            color={subject?.taser ? 'primary' : 'default'}
          >
            <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            subject?.firearms ? (
              <Stack>
                <Typography sx={{ fontSize: 11 }}>
                  Firearms officers only
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {subject.firearms.username}
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                  {format(subject.firearms.time, 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Stack>
            ) : (
              'No Firearms authority'
            )
          }
        >
          <IconButton
            size="small"
            onClick={
              canEdit
                ? onOptionClick(
                    subjectType,
                    personId,
                    occurrenceId,
                    subject?.code,
                    'firearms',
                  )
                : undefined
            }
            color={subject?.firearms ? 'primary' : 'default'}
          >
            <PistolIcon sx={{ fontSize: 'inherit' }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
